import { useLocale } from 'next-intl';
import { useAtom } from 'jotai';
import { isDesktop } from 'react-device-detect';
import { useRouter } from '@/navigation';
import type { SportMatchModel } from '@nf/types/odds';
import { type OddsPageLayout, ViewEnum } from '@/types';
import { KafkaNote, StreamingKafkaNote } from '@nf/utils-common/constants';
import { useAuthToken } from '@/store/auth';
import { useAppearanceStore } from '@/store/appearance';
import { pageSideMatchIdAtom } from '@/atoms/odds-page';
import { StreamingType, useStreamingStore } from '@/store/streaming';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { sportsbookService } from '@/services/sportsbook.service';
import { streamingService } from '@/services/streaming.service';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { useAccountInfo } from '@/hooks/query/account';
import { useSlmtGv } from '@/hooks/query/use-slmt-gv';
import { useGetEnableMatchList } from '@/hooks/query/use-promotion-data';
import { setKafkaNormal, setKafkaStreaming } from '@/hooks/query/use-kafka-log';
import { useBreakpoint } from '@/features/breakpoint/contexts';
import { Breakpoint } from '@/features/breakpoint';
import { GvIcon, StreamerIcon, VideoIcon } from '@/components/icons/svg-icons';
import { SymbolGvIcon, SymbolStreamerIcon, SymbolVideoIcon } from '@/components/icons/symbol-icon';
import { StreamingButton } from './components/streaming-button';
import { useGoldenDuelSetting } from '@/hooks/query/golden-duel';

interface StreamingControlsProps {
	match: SportMatchModel;
	oddsPageLayout: OddsPageLayout;
}

export const StreamingControls = ({ match, oddsPageLayout }: StreamingControlsProps) => {
	const router = useRouter();
	const locale = useLocale();
	const { SiteConfigsData: siteConfig } = useGetSiteConfigs();
	const view = useAppearanceStore(state => state.view);
	const { breakpoint } = useBreakpoint();
	const { user } = useAccountInfo();
	const authToken = useAuthToken();
	const isBefore = !Boolean(authToken);
	const [pageSideMatchId, setPageSideMatchId] = useAtom(pageSideMatchIdAtom);

	const { MatchId, MaT, GameID, LeagueId } = match;
	const isSideMatch = pageSideMatchId === MatchId;

	const singleMatchPath = sportsbookService.makeSingleMatchPath({
		gameId: GameID,
		maT: MaT,
		leagueId: LeagueId,
		matchId: MatchId,
	});

	const [streamingType, setStreamingType, setStreamingStatus] = useStreamingStore(
		state => [state.streamingType, state.setStreamingType, state.setStreamingStatus],
	);
	const { hasVideo, hasStreamer } = streamingService.getVideoOptions(match, true, siteConfig, user);
	const { hasSlmtGv } = useSlmtGv({ match, sport: String(GameID) });
	const { data: jackpotEnableMatchList } = useGetEnableMatchList();
	const { isGoldenDuelSupported } = useGoldenDuelSetting();

	const streamingButtons = [
		{
			condition: hasStreamer !== 0 && !isBefore,
			type: 'Streamer',
			Icon: StreamerIcon,
			DesktopIcon: SymbolStreamerIcon
		},
		{
			condition: hasVideo && !isBefore,
			type: 'Streaming',
			Icon: VideoIcon,
			DesktopIcon: SymbolVideoIcon
		},
		{
			condition: hasSlmtGv && !isBefore,
			type: 'SlmtGV',
			Icon: GvIcon,
			DesktopIcon: SymbolGvIcon
		}
	] as const;

	const handleVisitSingleMatch = () => {
		router.push(singleMatchPath), { locale };
		setStreamingStatus('', '', []);
	}

	const handleClick = (type: StreamingType) => {
		switch (type) {
			case 'Streaming':
				setKafkaTypeStreaming();
				break;
			case 'Streamer':
				setKafkaTypeSteamer();
				break;
		}

		setPromotionKafka();

		if (isDesktop) {
			setPageSideMatchId(MatchId);
		} else {
			handleVisitSingleMatch();
		}

		if (match.IsLive) {
			setStreamingType(type);
		}
	};

	const setKafkaTypeStreaming = () => {
		setKafkaNormal({
			note: KafkaNote.GalaxyV2,
			type: 3,
			action: 11
		});
	};

	const setKafkaTypeSteamer = () => {
		// 20 be 1, 21 be 2, 28 & 29 be 6
		let streamerType = 0;
		if (hasStreamer && hasStreamer > 19) {
			switch (hasStreamer) {
				case 20:
				case 21:
				case 22:
				case 23:
					streamerType = hasStreamer - 19;
					break;
				case 24:
				case 25:
				case 26:
				case 27:
				case 28:
				case 29:
					streamerType = (hasStreamer === 28 || hasStreamer === 29) ? 6 : hasStreamer - 23;
					break;
				default:
					streamerType = 0;
					break;
			}
		};

		setKafkaStreaming({
			note: StreamingKafkaNote.SingleMatch,
			type: 9,
			action: 1,
			matchId: MatchId,
			streamer_type: streamerType
		});
	};

	const setPromotionKafka = () => {
		// jackpot
		if (siteConfig?.EnableSingleMatchJackpot && (jackpotEnableMatchList?.find(x => x.MatchId === MatchId) ?? false)) {
			setKafkaNormal({
				note: KafkaNote.EPLJackpot,
				type: view == ViewEnum.mars ? 2 : 1,
				action: breakpoint <= Breakpoint.sm ? 8 : 22
			});
		}

		// golden duel
		if (isGoldenDuelSupported(GameID, LeagueId)) {
			setKafkaNormal({
				note: KafkaNote.GoldenDuel,
				type: 1,
				action: 45
			});
		}
	}

	return matchEquals(oddsPageLayout)
		.on('card', () => {
			return streamingButtons.map(btn =>
				btn.condition ? (
					<StreamingButton
						key={btn.type}
						type={btn.type as StreamingType}
						isActive={isSideMatch && streamingType === btn.type}
						isDesktop={isDesktop}
						Icon={btn.Icon}
						DesktopIcon={btn.DesktopIcon}
						onClick={handleClick}
					/>
				) : null
			);
		})
		.on('list', () => {
			const activeButton = streamingButtons.find(btn => btn.condition);

			return activeButton ? (
				<StreamingButton
					type={activeButton.type as StreamingType}
					isActive={isSideMatch && streamingType === activeButton.type}
					isDesktop={isDesktop}
					Icon={activeButton.Icon}
					DesktopIcon={activeButton.DesktopIcon}
					onClick={handleClick}
				/>
			) : null;
		})
		.otherwise(() => null);
};